import React from "react";
import { TEXT_TYPES } from "../../../utils/Constants";
import * as globalStyles from "../../../utils/GlobalStyles";
import IconView from "../IconView";

/*
  exampleTextObject = {
    text,
    type,
    color,
    isBold (optional),
    color (needed for fatHighlight and body)
  }

  if attributedTextArray prop composed of textObjects, returns array of text elements
  else takes props of a textObject, returns a single text element

  also takes prop for container style
*/
export default function LabelView(props) {
  const { style, attributedTextArray, wrappedRightIcon } = props;
  let usedAttributedTextArray = attributedTextArray;
  if (usedAttributedTextArray && !Array.isArray(usedAttributedTextArray))
    usedAttributedTextArray = [attributedTextArray];
  return (
    <div style={style}>
      {usedAttributedTextArray
        ? handleAttributedText(usedAttributedTextArray)
        : handleTextType(props)}
      {wrappedRightIcon && (
        <IconView {...wrappedRightIcon} style={styles.wrappedRightIcon} />
      )}
    </div>
  );
}

function handleTextType(textObject, key = "key") {
  const {
    text,
    type, // ['sectionHeader', 'itemHeader', 'dummyLink', 'fatHighlight', 'fatPrimary', 'body']
    color,
    isBold = false,
    oneLine = false,
    hasNewLines = false,
    textDecoration = "none",
    textTransform,
    background = "none",
  } = textObject;

  const oneLineStyle = oneLine
    ? {
        whiteSpace: "nowrap",
        overflow: "hidden",
      }
    : {
        whiteSpace: hasNewLines ? "pre-wrap" : undefined,
      };

  const dynamicStyle = {
    fontFamily: globalStyles.fonts.primary,
    fontSize: "15px",
    color: globalStyles.colors.text1,
    ...styles[type],
    textDecoration: textDecoration,
    // adding default overflow ellipsis behavior, but can override potentially
    // with an extra prop
    textOverflow: "ellipsis",
    ...oneLineStyle,
    background,
  };
  if (isBold) {
    dynamicStyle.fontWeight = globalStyles.fontWeights.extraBold;
  }
  if (color) {
    dynamicStyle.color = color;
  }
  if (textTransform) {
    dynamicStyle.textTransform = textTransform;
  }
  // switch handles what type of tag to use
  switch (type) {
    case TEXT_TYPES.TITLE:
      return (
        <h1 key={key} style={dynamicStyle}>
          {text}
        </h1>
      );
    case TEXT_TYPES.SECTION_HEADER:
      return (
        <h2 key={key} style={dynamicStyle}>
          {text}
        </h2>
      );
    case TEXT_TYPES.ITEM_HEADER:
      return (
        <h3 key={key} style={dynamicStyle}>
          {text}
        </h3>
      );
    default:
      // this span will have a specific style for different text types
      return (
        <span key={key} style={dynamicStyle}>
          {text}
        </span>
      );
  }
}

// concern about unique key usage
// https://medium.com/@robinpokorny/index-as-a-key-is-an-anti-pattern-e0349aece318
//
// however, key only needs to be unique compared to siblings
// https://stackoverflow.com/questions/33428962/how-unique-should-a-react-component-key-be
function handleAttributedText(attributedTextArray) {
  return attributedTextArray?.map((textObject, idx) =>
    handleTextType(textObject, idx)
  );
}

// all styles can have a defualt color because they can just be overriden by prop 'color'
const styles = {
  title: {
    color: globalStyles.colors.text1,
    textTransform: "uppercase",
    fontSize: "36px",
  },
  giantTitle: {
    color: globalStyles.colors.text1,
    fontSize: "50px",
    letterSpacing: "0.05em",
  },
  smallTitle: {
    fontSize: "15px",
    textTransform: "uppercase",
  },
  sectionHeader: {
    color: globalStyles.colors.text3,
    textTransform: "uppercase",
    letterSpacing: "0.2em",
    fontSize: "12px",
    lineHeight: "normal",
  },
  sectionHeaderLarge: {
    color: globalStyles.colors.text1,
    fontSize: "19px",
    lineHeight: "normal",
  },
  sectionHeaderExtraLarge: {
    color: globalStyles.colors.text1,
    fontSize: "22px",
    lineHeight: "normal",
  },
  sectionHeaderGodzilla: {
    color: globalStyles.colors.text1,
    fontSize: "27px",
    lineHeight: "normal",
  },
  sectionHeaderSmall: {
    color: globalStyles.colors.text3,
    textTransform: "uppercase",
    letterSpacing: "0.2em",
    fontSize: "10px",
    lineHeight: "normal",
  },
  itemHeader: {
    color: globalStyles.colors.text1,
    lineHeight: "20px",
    fontSize: "21px",
    fontWeight: globalStyles.fontWeights.thin,
    textTransform: "capitalize",
  },
  itemHeaderSmall: {
    color: globalStyles.colors.text1,
    lineHeight: "20px",
    fontSize: "18px",
    fontWeight: globalStyles.fontWeights.thin,
    textTransform: "capitalize",
  },
  superHeader: {
    color: globalStyles.colors.text1,
    lineHeight: "normal",
    fontSize: "21px",
    letterSpacing: "0.02em",
  },
  superHeaderLarge: {
    color: globalStyles.colors.text1,
    lineHeight: "normal",
    fontSize: "25px",
    letterSpacing: "0.02em",
  },
  superHeaderExtraLarge: {
    color: globalStyles.colors.text1,
    lineHeight: "normal",
    fontSize: "35px",
    letterSpacing: "0.02em",
  },
  superHeaderGodzilla: {
    color: globalStyles.colors.text1,
    lineHeight: "normal",
    fontSize: "45px",
    letterSpacing: "0.02em",
  },
  superHeaderSuperGodzilla: {
    color: globalStyles.colors.text1,
    lineHeight: "normal",
    fontSize: "60px",
    letterSpacing: "0.02em",
  },
  dummyLink: {
    color: globalStyles.primaryColor,
  },
  fatHighlight: {
    fontSize: "36px",
    fontWeight: globalStyles.fontWeights.extraBold,
  },
  fatHighlightMediumLarge: {
    fontSize: 30,
    fontWeight: globalStyles.fontWeights.extraBold,
  },
  fatHighlightMedium: {
    fontSize: 24,
    fontWeight: "600",
  },
  fatHighlightMediumSemi: {
    fontSize: 22,
    fontWeight: "600",
  },
  fatHighlightSmall: {
    fontSize: 18,
    fontWeight: globalStyles.mobileFontWeights.extraBold,
  },
  fatPrimary: {
    fontSize: "20px",
    fontWeight: globalStyles.fontWeights.extraBold,
    color: globalStyles.primaryColor,
  },
  bigBody: {
    fontSize: "16px",
    lineHeight: "22px",
    color: globalStyles.colors.text3,
  },
  smallBody: {
    fontSize: "13px",
    color: globalStyles.colors.text3,
  },
  tallBody: {
    fontSize: "15px",
    lineHeight: "20px",
    color: globalStyles.colors.text3,
  },
  body: {
    fontSize: "15px",
    color: globalStyles.colors.text3,
  },
  giantBody: {
    fontSize: "24px",
    lineHeight: "28px",
    letterSpacing: 0.2,
    color: globalStyles.colors.text3,
  },
  small: {
    fontSize: "12px",
    color: globalStyles.colors.text3,
  },
  tiny: {
    fontSize: "10px",
    color: globalStyles.colors.text3,
  },
  wrappedRightIcon: {
    marginLeft: 6,
  },
  listItem: {
    fontSize: "16px",
  },
  listBold: {
    fontSize: "20px",
    fontWeight: globalStyles.fontWeights.extraBold,
  },
};
